/**
 * Two-Panel Mobile Menu
 * Features: Tab switching, drill-down navigation, keyboard support
 */

(function() {
  'use strict';

  class TwoPanelMenu {
    constructor(container) {
      this.container = container;

      // Elements
      this.tabs = container.querySelectorAll('.two-panel-menu__tab');
      this.panels = container.querySelectorAll('.two-panel-menu__panel');
      this.drillStack = []; // Track drill-down navigation stack
      this.currentTabIndex = 0;

      this.init();
    }

    init() {
      this.bindTabEvents();
      this.bindDrillEvents();
      this.bindKeyboardEvents();
      this.setItemIndexes();
    }

    // Set CSS custom property for staggered animations
    setItemIndexes() {
      this.panels.forEach(panel => {
        const items = panel.querySelectorAll('.two-panel-menu__item');
        items.forEach((item, index) => {
          item.style.setProperty('--item-index', index);
        });
      });
    }

    // ==========================================================================
    // Tab Switching
    // ==========================================================================

    bindTabEvents() {
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.switchTab(index));
      });
    }

    switchTab(index) {
      if (index === this.currentTabIndex) return;

      // Deactivate current tab and panel
      this.tabs[this.currentTabIndex].classList.remove('is-active');
      this.tabs[this.currentTabIndex].setAttribute('aria-selected', 'false');
      this.panels[this.currentTabIndex].classList.remove('is-active');
      this.panels[this.currentTabIndex].hidden = true;

      // Activate new tab and panel
      this.tabs[index].classList.add('is-active');
      this.tabs[index].setAttribute('aria-selected', 'true');
      this.panels[index].classList.remove('is-active');
      this.panels[index].hidden = false;

      // Force reflow before adding is-active for animation
      void this.panels[index].offsetWidth;
      this.panels[index].classList.add('is-active');

      // Reset drill-down state for new panel
      this.resetDrillState(this.panels[index]);

      this.currentTabIndex = index;
      this.drillStack = [];
    }

    resetDrillState(panel) {
      const levels = panel.querySelectorAll('.two-panel-menu__drill-level');
      levels.forEach(level => {
        if (level.dataset.drillLevel === '0') {
          level.classList.add('is-active');
          level.classList.remove('is-exiting');
          level.hidden = false;
        } else {
          level.classList.remove('is-active', 'is-exiting');
          level.hidden = true;
        }
      });
    }

    // ==========================================================================
    // Drill-down Navigation
    // ==========================================================================

    bindDrillEvents() {
      // Drill triggers (forward navigation)
      this.container.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-drill-trigger]');
        if (trigger) {
          e.preventDefault();
          this.drillForward(trigger);
        }

        const backBtn = e.target.closest('[data-drill-back]');
        if (backBtn) {
          e.preventDefault();
          this.drillBack();
        }
      });
    }

    drillForward(trigger) {
      const targetId = trigger.dataset.drillTarget;
      const panel = trigger.closest('.two-panel-menu__panel');
      const targetLevel = panel.querySelector(`[data-drill-id="${targetId}"]`);

      if (!targetLevel) return;

      // Find current active level
      const currentLevel = panel.querySelector('.two-panel-menu__drill-level.is-active:not(.is-exiting)');

      if (currentLevel) {
        // Mark current as exiting
        currentLevel.classList.add('is-exiting');

        // Store in stack for back navigation
        this.drillStack.push({
          level: currentLevel,
          scrollTop: currentLevel.querySelector('[data-list-scroll]')?.scrollTop || 0
        });
      }

      // Activate target level
      targetLevel.hidden = false;
      // Force reflow
      void targetLevel.offsetWidth;
      targetLevel.classList.add('is-active');

      // Update trigger aria state
      trigger.setAttribute('aria-expanded', 'true');

      // After transition, hide current level
      setTimeout(() => {
        if (currentLevel) {
          currentLevel.classList.remove('is-active');
          currentLevel.hidden = true;
          currentLevel.classList.remove('is-exiting');
        }
      }, 300);
    }

    drillBack() {
      if (this.drillStack.length === 0) return;

      const panel = this.panels[this.currentTabIndex];
      const currentLevel = panel.querySelector('.two-panel-menu__drill-level.is-active:not(.is-exiting)');
      const previousState = this.drillStack.pop();
      const previousLevel = previousState.level;

      if (currentLevel) {
        currentLevel.classList.remove('is-active');
      }

      // Show and activate previous level
      previousLevel.hidden = false;
      previousLevel.classList.remove('is-exiting');
      // Force reflow
      void previousLevel.offsetWidth;
      previousLevel.classList.add('is-active');

      // Restore scroll position
      const scrollContainer = previousLevel.querySelector('[data-list-scroll]');
      if (scrollContainer && previousState.scrollTop) {
        scrollContainer.scrollTop = previousState.scrollTop;
      }

      // Reset trigger aria state if applicable
      const drillId = currentLevel?.dataset.drillId;
      if (drillId) {
        const trigger = panel.querySelector(`[data-drill-target="${drillId}"]`);
        if (trigger) {
          trigger.setAttribute('aria-expanded', 'false');
        }
      }

      // Hide current level after transition
      setTimeout(() => {
        if (currentLevel) {
          currentLevel.hidden = true;
        }
      }, 300);
    }

    // ==========================================================================
    // Keyboard Navigation
    // ==========================================================================

    bindKeyboardEvents() {
      this.container.addEventListener('keydown', (e) => {
        // Tab navigation with arrow keys
        if (document.activeElement.closest('.two-panel-menu__tabs')) {
          this.handleTabKeyboard(e);
        }

        // Escape to go back in drill-down
        if (e.key === 'Escape') {
          if (this.drillStack.length > 0) {
            e.preventDefault();
            this.drillBack();
          }
        }
      });
    }

    handleTabKeyboard(e) {
      const tabsArray = Array.from(this.tabs);
      const currentIndex = tabsArray.indexOf(document.activeElement);

      if (currentIndex === -1) return;

      let newIndex = currentIndex;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          newIndex = (currentIndex + 1) % tabsArray.length;
          break;
        case 'ArrowUp':
          e.preventDefault();
          newIndex = (currentIndex - 1 + tabsArray.length) % tabsArray.length;
          break;
        case 'Home':
          e.preventDefault();
          newIndex = 0;
          break;
        case 'End':
          e.preventDefault();
          newIndex = tabsArray.length - 1;
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          this.switchTab(currentIndex);
          return;
        default:
          return;
      }

      tabsArray[newIndex].focus();
    }
  }

  // ==========================================================================
  // Initialization
  // ==========================================================================

  function initTwoPanelMenus() {
    document.querySelectorAll('[data-two-panel-menu]').forEach(container => {
      if (!container._twoPanelMenu) {
        container._twoPanelMenu = new TwoPanelMenu(container);
      }
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTwoPanelMenus);
  } else {
    initTwoPanelMenus();
  }

  // Re-initialize when Shopify section is reloaded (for theme editor)
  document.addEventListener('shopify:section:load', (e) => {
    const container = e.target.querySelector('[data-two-panel-menu]');
    if (container) {
      container._twoPanelMenu = new TwoPanelMenu(container);
    }
  });

  // Also listen for menu drawer opening to ensure initialization
  document.addEventListener('menu-drawer:open', initTwoPanelMenus);
})();
